import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MoreVertical,
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  Trash2,
  ArrowUpDown,
  ShieldMinus,
  RefreshCw,
} from "lucide-react";
import Loader from "../Loader";
import { toast } from "react-toastify";
import moment from "moment";
import ViewTeamMemberModal from "./ViewTeamMemberModal";
import Avatar from "./Avatar";
import EmptyState from "./EmptyState";
import ConfirmationModal from "./ConfirmationModal";
import { useRolesAndPermissions } from "../../hooks/permissionHooks/useRolesAndPermissions";

const LoadingRow = () => (
  <tr className="border-b border-gray-50">
    {[...Array(7)].map((_, index) => (
      <td key={index} className="px-4 py-3">
        <div className="animate-pulse">
          <div className="w-full h-4 bg-gray-200 rounded"></div>
        </div>
      </td>
    ))}
  </tr>
);

const RolesTable = ({
  setTotalUsers = () => {},
  onViewUser = () => {},
  handleStatusChange = () => {},
  handleDeleteNotification = () => {},
  handleEditModal = () => {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [activeDropup, setActiveDropup] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [viewModal, setViewModal] = useState({
    isOpen: false,
    teamMember: null,
  });
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    type: "delete",
    teamMember: null,
    onConfirm: null,
  });
  const itemsPerPage = 10;

  //Todo => Use our existing hook properly
  const {
    teamMembers,
    totalMembers,
    isFetchingMembers,
    isUpdating,
    isDeleting,
    updateTeamMemberStatus,
    deleteTeamMember,
    bulkUpdateStatus,
    searchTeamMembers,
    refreshTeamData,
  } = useRolesAndPermissions();

  //Todo => Update parent component with total users count
  useEffect(() => {
    if (setTotalUsers && typeof setTotalUsers === "function") {
      setTotalUsers(totalMembers);
    }
  }, [totalMembers, setTotalUsers]);

  //Todo => Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeDropup &&
        !event.target.closest(".action-dropdown-container") &&
        !event.target.closest("[data-dropdown-menu]")
      ) {
        setActiveDropup(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropup]);

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special cases
      if (sortConfig.key === "createdOn") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  const requestSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = searchTerm ? searchTeamMembers(searchTerm) : teamMembers;
    return sortData(filtered);
  }, [teamMembers, searchTerm, sortConfig, searchTeamMembers]);

  //Todo => Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  //Todo => Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  //Todo => Column headers
  const columns = [
    { key: "fullName", label: "Full Name", includeAvatar: true },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "role", label: "Role" },
    { key: "createdOn", label: "Created On" },
    { key: "status", label: "Status" },
    { key: "action", label: "Action" },
  ];

  const handleDeleteNotify = (user) => {
    setConfirmationModal({
      isOpen: true,
      type: "delete",
      teamMember: user,
      onConfirm: async () => {
        try {
          await deleteTeamMember(user.vendorSlug || user.vendor_slug);
          if (handleDeleteNotification) handleDeleteNotification(user);
          setConfirmationModal({
            isOpen: false,
            type: "delete",
            teamMember: null,
            onConfirm: null,
          });
        } catch (error) {
          toast.error("Failed to delete user");
        }
      },
    });
    setActiveDropup(null);
  };

  const handleUserEdit = (user) => {
    if (handleEditModal) handleEditModal(user);
    setActiveDropup(null);
  };

  const handleViewUser = (user) => {
    setViewModal({ isOpen: true, teamMember: user });
    setActiveDropup(null);
  };

  const handleUserStatusChange = async (type, user) => {
    const newStatus = type === "disable" ? "inactive" : "active";
    const modalType = newStatus === "active" ? "activate" : "deactivate";

    setConfirmationModal({
      isOpen: true,
      type: modalType,
      teamMember: user,
      onConfirm: async () => {
        try {
          await updateTeamMemberStatus({
            userId: user.id,
            status: newStatus,
          });
          if (handleStatusChange) handleStatusChange(type, user);
          setConfirmationModal({
            isOpen: false,
            type: "delete",
            teamMember: null,
            onConfirm: null,
          });
        } catch (error) {
          toast.error("Failed to update user status");
        }
      },
    });
    setActiveDropup(null);
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    setConfirmationModal({
      isOpen: true,
      type: "delete",
      teamMember: null,
      onConfirm: async () => {
        try {
          const promises = selectedRows.map((id) => {
            const user = teamMembers.find((member) => member.id === id);
            return deleteTeamMember(user?.vendorSlug || user?.vendor_slug);
          });
          await Promise.all(promises);
          setSelectedRows([]);
          toast.success(`${selectedRows.length} user(s) deleted successfully`);
          setConfirmationModal({
            isOpen: false,
            type: "delete",
            teamMember: null,
            onConfirm: null,
          });
        } catch (error) {
          toast.error("Failed to delete selected users");
        }
      },
    });
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedRows.length === 0) return;

    try {
      await bulkUpdateStatus(selectedRows, status);
      setSelectedRows([]);
      toast.success(
        `${selectedRows.length} user(s) ${
          status === "active" ? "activated" : "deactivated"
        } successfully`
      );
    } catch (error) {
      toast.error("Failed to update selected users");
    }
  };

  //Todo => Helper function to check if user is active
  const isUserActive = (user) => {
    return user.status === "Active" || user.status === "active";
  };

  //Todo => Helper function to get display name
  const getDisplayName = (user) => {
    return user.fullName || user.full_name || "";
  };

  //Todo => Helper function to format status for display
  const getDisplayStatus = (user) => {
    const isActive = isUserActive(user);
    return isActive ? "Active" : "Inactive";
  };

  //Todo => Helper function to get created date
  const getCreatedDate = (user) => {
    return user.createdOn || user.created_at;
  };

  //Todo => Helper function to export data to CSV
  const handleExport = () => {
    const csvContent = [
      ["Full Name", "Email", "Phone", "Role", "Status", "Created On"],
      ...teamMembers.map((user) => [
        getDisplayName(user),
        user.email,
        user.phone,
        user.role,
        getDisplayStatus(user),
        getCreatedDate(user),
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team_members.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isFetchingMembers && teamMembers.length === 0) {
    return <Loader />;
  }

  return (
    <div className="p-6 space-y-4 w-full bg-white rounded-xl shadow-sm">
      {/* //Todo  Search and Actions Bar */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 text-gray-400 -translate-y-1/2"
            size={20}
          />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="py-2 pr-4 pl-10 w-full rounded-xl border-2 border-gray-100 transition-all focus:border-pink-200 focus:ring-2 focus:ring-pink-100 focus:outline-none"
          />
        </div>

        {/* //Todo => Action Buttons */}
        <div className="flex gap-3 items-center">
          {selectedRows.length > 0 && (
            <>
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="inline-flex gap-2 items-center px-4 py-2 text-red-600 bg-red-50 rounded-xl border border-red-100 transition-all duration-200 hover:bg-red-100 hover:shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={18} />
                <span className="hidden sm:inline">
                  Delete ({selectedRows.length})
                </span>
              </button>

              <div className="relative dropdown-container">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkStatusUpdate(e.target.value);
                      e.target.value = "";
                    }
                  }}
                  className="px-4 py-2 text-blue-600 bg-blue-50 rounded-xl border border-blue-100 transition-all duration-200 cursor-pointer hover:bg-blue-100 hover:shadow-sm"
                  defaultValue=""
                >
                  <option value="" disabled>
                    Bulk Status
                  </option>
                  <option value="active">Activate Selected</option>
                  <option value="inactive">Deactivate Selected</option>
                </select>
              </div>
            </>
          )}

          <button
            onClick={refreshTeamData}
            disabled={isFetchingMembers}
            className="inline-flex gap-2 items-center px-4 py-2 text-gray-600 bg-gray-50 rounded-xl border border-gray-100 transition-all duration-200 hover:bg-gray-100 hover:shadow-sm active:scale-95 disabled:opacity-50"
          >
            <RefreshCw
              size={18}
              className={isFetchingMembers ? "animate-spin" : ""}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* //Todo => Loading overlay */}
      {(isUpdating || isDeleting) && (
        <div className="flex absolute inset-0 z-10 justify-center items-center rounded-xl backdrop-blur-sm bg-white/50">
          <div className="flex gap-2 items-center px-4 py-2 bg-white rounded-lg shadow-lg">
            <div className="w-4 h-4 border-2 border-[#FF6C6C] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600">
              {isUpdating ? "Updating..." : "Deleting..."}
            </span>
          </div>
        </div>
      )}

      {/* //Todo => Table */}
      {teamMembers.length === 0 && !isFetchingMembers ? (
        <EmptyState onRefresh={refreshTeamData} />
      ) : (
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b-2 border-gray-100">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-sm font-semibold text-left text-gray-600 cursor-pointer hover:text-pink-500"
                    onClick={() =>
                      column.key !== "action" && requestSort(column.key)
                    }
                  >
                    <div className="flex gap-2 items-center">
                      {column.label}
                      {column.key !== "action" && <ArrowUpDown size={14} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isFetchingMembers && teamMembers.length > 0
                ? [...Array(3)].map((_, index) => <LoadingRow key={index} />)
                : paginatedData.map((user) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-gray-50 hover:bg-gray-50/50"
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="relative px-4 py-3 text-sm text-gray-600"
                        >
                          {column.key === "action" ? (
                            <div className="relative action-dropdown-container">
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (activeDropup === user.id) {
                                    setActiveDropup(null);
                                    return;
                                  }

                                  const button = e.currentTarget;
                                  const rect = button.getBoundingClientRect();
                                  const scrollTop =
                                    window.pageYOffset ||
                                    document.documentElement.scrollTop;
                                  const scrollLeft =
                                    window.pageXOffset ||
                                    document.documentElement.scrollLeft;

                                  setDropdownPosition({
                                    top: rect.top + scrollTop - 160,
                                    left: rect.left + scrollLeft - 192,
                                  });

                                  setActiveDropup(user.id);
                                }}
                                className="p-2 rounded-lg transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-600" />
                              </motion.button>
                            </div>
                          ) : column.key === "fullName" ? (
                            <div className="flex gap-3 items-center">
                              <Avatar
                                src={user.avatar}
                                name={getDisplayName(user)}
                              />
                              <span>{getDisplayName(user)}</span>
                            </div>
                          ) : column.key === "status" ? (
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${
                                isUserActive(user)
                                  ? "text-green-600 bg-green-100"
                                  : "text-red-600 bg-red-100"
                              }`}
                            >
                              {getDisplayStatus(user)}
                            </span>
                          ) : column.key === "createdOn" ? (
                            moment(getCreatedDate(user)).format("DD-MMM-YYYY")
                          ) : (
                            user[column.key]
                          )}
                        </td>
                      ))}
                    </motion.tr>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {/* //Todo => Fixed Position Dropdown - Outside table structure */}
      <AnimatePresence>
        {activeDropup && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setActiveDropup(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              style={{
                position: "fixed",
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                zIndex: 50,
              }}
              className="py-2 w-48 bg-white rounded-xl border border-gray-200 shadow-lg"
              data-dropdown-menu="true"
            >
              {(() => {
                const user = teamMembers.find((u) => u.id === activeDropup);
                if (!user) return null;

                return (
                  <>
                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropup(null);
                        handleViewUser(user);
                      }}
                      className="flex gap-3 items-center px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <Eye className="w-4 h-4 text-blue-500" />
                      <span>View Details</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropup(null);
                        handleUserEdit(user);
                      }}
                      className="flex gap-3 items-center px-4 py-2 w-full text-sm text-left text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <Edit className="w-4 h-4 text-green-500" />
                      <span>Edit Member</span>
                    </motion.button>
                    <hr className="my-1 border-gray-100" />

                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropup(null);
                        handleDeleteNotify(user);
                      }}
                      className="flex gap-3 items-center px-4 py-2 w-full text-sm text-left text-red-600 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Member</span>
                    </motion.button>
                  </>
                );
              })()}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* //Todo => Pagination */}
      {filteredAndSortedData.length > 0 && (
        <div className="flex justify-between items-center pt-4">
          <p className="text-sm text-gray-500">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)}{" "}
            of {filteredAndSortedData.length} entries
          </p>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Page numbers */}
            <div className="flex gap-1 items-center">
              {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                const pageNumber =
                  currentPage <= 3 ? index + 1 : currentPage - 2 + index;
                if (pageNumber > totalPages) return null;

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                      currentPage === pageNumber
                        ? "bg-[#FF6C6C] text-white"
                        : "hover:bg-gray-100 text-gray-600"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* //Todo => View Team Member Modal */}
      <ViewTeamMemberModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, teamMember: null })}
        teamMember={viewModal.teamMember}
        onEdit={handleUserEdit}
      />

      {/* //Todo => Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          setConfirmationModal({
            isOpen: false,
            type: "delete",
            teamMember: null,
            onConfirm: null,
          })
        }
        onConfirm={confirmationModal.onConfirm}
        type={confirmationModal.type}
        teamMember={confirmationModal.teamMember}
        isLoading={isDeleting || isUpdating}
      />
    </div>
  );
};

export default RolesTable;
